import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

import {
  fetchSession,
  logoutRequest,
  type SessionResponse,
  type SessionUser,
} from "../services/authService";


type AuthStatus = "loading" | "authenticated" | "unauthenticated";

type AuthContextValue = {
  status: AuthStatus;
  user: SessionUser | null;
  ssoEnabled: boolean;
  refreshSession: () => Promise<SessionResponse>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function unauthenticatedSession(ssoEnabled = false): SessionResponse {
  return {
    authenticated: false,
    user: null,
    sso_enabled: ssoEnabled,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  const refreshSession = async (): Promise<SessionResponse> => {
    try {
      const session = await fetchSession();
      setUser(session.user);
      setSsoEnabled(session.sso_enabled);
      setStatus(session.authenticated ? "authenticated" : "unauthenticated");
      return session;
    } catch (error) {
      console.error("Could not load session state:", error);
      const fallbackSession = unauthenticatedSession(ssoEnabled);
      setUser(null);
      setStatus("unauthenticated");
      return fallbackSession;
    }
  };

  useEffect(() => {
    const loadSession = async () => {
      await refreshSession();
    };

    void loadSession();
  }, []);

  const logout = async () => {
    try {
      await logoutRequest();
    } finally {
      setUser(null);
      setStatus("unauthenticated");
    }
  };

  const contextValue: AuthContextValue = {
    status,
    user,
    ssoEnabled,
    refreshSession,
    logout,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider.");
  }
  return context;
}
